import {
    faChevronLeft,
    faChevronRight,
} from '@fortawesome/free-solid-svg-icons'
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome'
import { PackageTemplate, RRDate, RRDuration } from '@raskrask-dk/raskrask-utils'
import { Button, Flex, Grid, Text } from '@raskrask-dk/rr-components'

import { t } from 'i18next'
import React, { Fragment, ReactFragment, useCallback } from 'react'
import { BasisTypeBF, GoalBF, PartnerBF, ServiceBF } from '../../../models'
import {
    CTAHelpText,
    StyledGoToCalendar,
    StyledGoToServicePicker,
} from '../../calendar/styles'
import { ListRender } from '../higherOrderComponents/ListRender'
import { ItemWithHeader } from '../ItemWithHeader'
import {
    RenderGoal,
    RenderServiceType,
    RenderPartnerCard,
    RenderPartnerKlippeCard,
} from '../mappers'

export const GoalComponent = (
    goals: GoalBF[],
    selectedGoal: GoalBF | undefined,
    setGoal: (goal: GoalBF) => void,
    basisTypeId: number
): React.ReactFragment => {
    return (
        <ItemWithHeader
            header={
                goals.length > 1
                    ? t(`bookingflow.calendar.goals.title.${basisTypeId}`)
                    : t(`bookingflow.calendar.goals.singletitle.${basisTypeId}`)
            }
            item={
                <ListRender
                    items={goals}
                    render={RenderGoal(selectedGoal as GoalBF, setGoal)}
                />
            }
            wrapper={
                <Flex
                    gap="8"
                    css={{
                        flexWrap: 'wrap',
                        '@mobile': { flexDirection: 'column' },
                    }}
                />
            }
        />
    )
}

export const ServiceTypesComponent = (
    services: ServiceBF[],
    selectedService: ServiceBF | undefined,
    setService: (service: ServiceBF) => void,
    basisTypeId: number
): React.ReactFragment => {
    return (
        <ItemWithHeader
            header={t(`bookingflow.calendar.servicetypes.title.${basisTypeId}`)}
            item={
                <ListRender
                    items={services.sort(
                        (a, b) => a.duration.toMillis() - b.duration.toMillis()
                    )}
                    render={RenderServiceType(selectedService, setService)}
                />
            }
            wrapper={
                <Flex
                    gap="8"
                    css={{
                        flexWrap: 'wrap',
                    }}
                />
            }
        />
    )
}

export const KlippeCardButton = (
    selectedGoal: GoalBF | undefined,
    selectedService: ServiceBF | undefined,
    onClickKlippeKortButtonHeaderDesktop: () => void,
    basisTypeId: number,
    isCompanyBooking: boolean,
    disabled?: boolean
): React.ReactFragment => {
    return !isCompanyBooking ? (
        <ItemWithHeader
            header={t(`bookingflow.calendar.package.btnheader.${basisTypeId}`)}
            item={
                (selectedGoal && selectedService && (
                    <Fragment>
                        <Button
                            active={false}
                            data-cy={`klippekort_button`}
                            disabled={disabled || false}
                            onClick={() => {
                                onClickKlippeKortButtonHeaderDesktop()
                            }}
                            size="large"
                            variant="option"
                        >
                            {t('calendar.package.btntext')}
                        </Button>
                    </Fragment>
                )) ?? <></>
            }
            wrapper={
                <Flex
                    gap="8"
                    css={{
                        flexWrap: 'wrap',
                    }}
                />
            }
        />
    ) : (
        <></>
    )
}

export const GoToCalendarComponent = (
    selectedGoal: GoalBF | undefined,
    selectedService: ServiceBF | undefined,
    onClickServiceCTAMobile: () => void,
    showHelpText: boolean
): React.ReactFragment => (
    <StyledGoToCalendar>
        {showHelpText && (
            <CTAHelpText>
                <Text>{t('calendar.gotocalendar.helptext')}</Text>
            </CTAHelpText>
        )}
        <Grid
            css={{
                alignContent: 'center',
                gridAutoFlow: 'column',
                justifyContent: 'space-between',
            }}
        >
            <Grid>
                <Text>{t(`common:goals.${selectedGoal?.id}`)}</Text>
                <Text bold size="xl">
                    {(selectedService?.duration?.toMillis() || 0) / 60 / 1000}{' '}
                    min
                </Text>
            </Grid>
            <Button
                data-cy={'serviceCtaMobile_booking'}
                disabled={selectedService === undefined}
                onClick={onClickServiceCTAMobile}
                size="large"
                variant="success"
                endIcon={<FontAwesomeIcon icon={faChevronRight} />}
            >
                {t('common:continue')}
            </Button>
        </Grid>
    </StyledGoToCalendar>
)

export const GoToServicePicker = (
    selectedGoal: GoalBF | undefined,
    selectedService: ServiceBF | undefined,
    onClickGoToServicePicker: () => void
): React.ReactFragment => {
    return (
        <StyledGoToServicePicker
            data-cy={'serviceBackBtn'}
            onClick={() => onClickGoToServicePicker()}
        >
            <FontAwesomeIcon icon={faChevronLeft} />
            <Text size="xl">
                {t(`common:goals.${selectedGoal?.id}`)} |{' '}
                {(selectedService?.duration?.toMillis() || 0) / 60 / 1000} min
            </Text>
        </StyledGoToServicePicker>
    )
}

export const PartnerPickerComponent = (
    selectedService: ServiceBF | undefined,
    basisType: BasisTypeBF,
    partners: PartnerBF[],
    selectedPartner: PartnerBF | undefined,
    setPartner: (partner: PartnerBF) => void,
    isMobile: boolean,
    isCompanyBooking: boolean,
    selectedDate: RRDate | undefined,
    selectedPackage?: PackageTemplate
): React.ReactFragment => {
    const duration = selectedService?.duration || new RRDuration({ minutes: 0 })
    const partnersSortedByTime = [...partners]
        .map((p) => {
            const pFirstAv = p.schedule.firstDayAvailable(duration)
            return {
                partner: p,
                firstAv: pFirstAv === 'NO_AVAILABLE' ? 0 : pFirstAv.toMillis(),
            }
        })
        .filter((p) => p.firstAv !== 0)
        .filter((p) => {
            return (
                selectedPackage === undefined ||
                p.partner.servicetypes.some(
                    (s) => s.id === selectedPackage?.service.id
                )
            )
        })
        .sort((p1, p2) => p1.firstAv - p2.firstAv)
        .map((p) => p.partner)

    const header = selectedDate
        ? t('calendar.partnercards.dateselected', {
              partners: t(`common:basistypes.${basisType.id}.pluralis`),
          })
        : t('calendar.partnercards.allshown', {
              partners: t(`common:basistypes.${basisType.id}.pluralis`),
          })

    const partnerCards = useCallback(
        (service: ServiceBF) => (
            <ListRender
                items={partnersSortedByTime}
                render={
                    selectedPackage
                        ? RenderPartnerKlippeCard(
                              selectedPartner,
                              selectedPackage,
                              setPartner,
                              service,
                              isMobile
                          )
                        : RenderPartnerCard(
                              selectedPartner,
                              setPartner,
                              service,
                              isMobile,
                              isCompanyBooking,
                              selectedDate
                          )
                }
                wrapper={
                    <Grid
                        autoFlow="dense"
                        gap="16"
                        css={{
                            '@desktop': {
                                gridTemplateColumns:
                                    'repeat(auto-fill, minmax(175px, 1fr))',
                            },
                        }}
                    />
                }
            />
        ),
        [
            isCompanyBooking,
            isMobile,
            selectedDate,
            selectedPackage,
            selectedPartner,
            setPartner,
            partnersSortedByTime,
        ]
    )

    return selectedService ? (
        <ItemWithHeader header={header} item={partnerCards(selectedService)} />
    ) : (
        <></>
    )
}

export const ServicePickerComponent = (
    goalsComponent: ReactFragment,
    servicesTypesComponent: ReactFragment,
    klippecardButton: ReactFragment
): React.ReactFragment => {
    return (
        <Flex
            gap={16}
            css={{
                '@desktop': {
                    justifyContent: 'space-between',
                    flexDirection: 'row',
                },

                '@mobile': {
                    flexDirection: 'column',
                },
            }}
        >
            {goalsComponent}
            {servicesTypesComponent}
            {klippecardButton}
        </Flex>
    )
}
